import './buttons.css';
import {Portal} from 'react-portal';
import PropTypes from 'prop-types';
import React, {ReactElement, useEffect, useMemo, useRef, useState} from 'react';
import {usePopper} from 'react-popper';

import {faAngleDown} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import PrimaryInverseButton from './PrimaryInverseButton';
import DangerButton from './DangerButton';
import Menu, {IfmsMenuItem} from './menu/Menu';
import BlackInverseButton from "./BlackInverseButton";
import {faAngleUp} from "@fortawesome/free-solid-svg-icons/faAngleUp";
import HighlightButton from "./HighlightButton";
import IconButton from './IconButton';

export interface ActionButtonItem {
  label: React.ReactNode|string|undefined,
  separator?: boolean,
  newCol?: boolean,
  popupComponent?: ReactElement,
  popupWidth?: number,
  popupLabel?: React.ReactNode|string|undefined,
  [x:string]: any,
  hidden?: boolean,
  link?: string,
  disabled? : boolean
};

export const borderColors = ['#0079A4', '#B5038F', '#FF3300', '#89C064', '#00B0F0', '#007BFF'];

export function splitMenuItemsIntoSections(actionButtonItems: ActionButtonItem[]): IfmsMenuItem[][][] {
  const result: IfmsMenuItem[][][] = [[[]]]
  actionButtonItems.forEach((item, index) => {
    if (item.hidden) return
    result[result.length - 1][result[result.length - 1].length - 1].push({
      text: item.label,
      // separator: item.separator,
      separator: false,
      selected: false,
      index,
      disabled:item?.disabled ?? false,
      name: item.name
    });

    if(item.newCol && index !== actionButtonItems.length - 1) {
      result.push([]);
    }
    if(item.separator && index !== actionButtonItems.length - 1) {
      result[result.length - 1].push([]);
    }
  })
  return result;
} 

const CLOSE_ALL_MENUS = 'CLOSE_ALL_MENUS';

function ActionButton({
  items = [],
  type = 'primary',
  size = 'regular',
  children = undefined,
  action = 'click',
  hideCaret = false,
  onItemSelected = () => {},
  height = undefined,
  variant = 'plain',
  name = undefined,
  tooltip = undefined,
  useInList = false,
}: {
  items?: Array<ActionButtonItem>,
  type?: 'primary'|'secondary'|'danger'|'primaryInverse'|'blackInverse'|'transparent'|'iconButton',
  size?: 'regular'|'small'|'large'|'extrasmall',
  action?: 'click'|'hover'|'rightClick',
  children?: React.ReactChild|React.ReactNode|string|null|undefined,
  hideCaret?: boolean,
  onItemSelected?: (x:ActionButtonItem) => void,
  height?: string|undefined,
  variant?: 'plain'|'highlight',
  name?: string|undefined,  
  useInList? : boolean|undefined,
  tooltip? : string|undefined
}) {
  const [menuVisible, _setMenuVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement|null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement|null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement|null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
  });

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef(false);

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideMenu, true);
    document.addEventListener('mousemove', mouseMove, true);
    document.addEventListener(CLOSE_ALL_MENUS, handleCloseMenu);
    return () => {
      document.removeEventListener('click', handleClickOutsideMenu, true);
      document.removeEventListener('mousemove', mouseMove, true);
      document.removeEventListener(CLOSE_ALL_MENUS, handleCloseMenu);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function mouseMove(e:MouseEvent) {
    if (action !== 'hover') {
      return;
    }

    const mX = e.clientX;
    const mY = e.clientY;

    let overButton = false;
    let overMenu = false;

    if (buttonRef && buttonRef.current) {
      const bc = buttonRef.current.getBoundingClientRect();
      overButton = mX >= bc.x && mX <= bc.right && mY >= bc.top && mY <= bc.bottom;
    }

    if (menuContainerRef && menuContainerRef.current) {
      const mc = menuContainerRef.current.getBoundingClientRect();
      overMenu = mX >= mc.x && mX <= mc.right && mY >= mc.top && mY <= mc.bottom;
    }

    const isJobSwitcingMenuOpend = document.querySelectorAll('[class*="ifms-custom-jobswitchinghelpermenu"]').length > 0;

    const showMenu = (overButton || overMenu) && !isJobSwitcingMenuOpend;
    if (showMenu !== openRef.current) {
      setMenuVisible(showMenu);
    }
  }

  function handleClickOutsideMenu(e:MouseEvent) {
    if (action === 'hover') {
      return;
    }

    if (openRef.current && menuContainerRef && menuContainerRef.current) {
      const target = e.target as HTMLElement;
      const shouldClose = !menuContainerRef.current.contains(target);

      if (shouldClose) {
        setMenuVisible(false);
      }
    }
  }

  function handleCloseMenu() {
    if (openRef.current) {
      _setMenuVisible(false);
      openRef.current = false;
    }
  }

  function setMenuVisible(v:boolean) {
    if (v) {
      document.dispatchEvent(new CustomEvent(CLOSE_ALL_MENUS));
    }

    openRef.current = v;
    _setMenuVisible(v);
  }

  function onMenuItemSelected(item:IfmsMenuItem) {
    if (item.index !== undefined && item.index >= 0 && item.index <= items.length) {
      onItemSelected(items[item.index]);
    }

    setMenuVisible(false);
  }

  let ButtonComponent = PrimaryButton;
  let HighlightButtonComponent = HighlightButton;

  switch (type) {
    case 'primary':
      ButtonComponent = PrimaryButton;
      HighlightButtonComponent = HighlightButton;
      break;
    case 'secondary':
      ButtonComponent = SecondaryButton;
      break;
    case 'danger':
      ButtonComponent = DangerButton;
      break;
    case 'primaryInverse':
      ButtonComponent = PrimaryInverseButton;
      break;
    case 'blackInverse':
      ButtonComponent = BlackInverseButton;
      break;
    case 'iconButton':
      ButtonComponent = IconButton;
      break;
    default:
      break;
  }

  const appliedPopperStyles = {
    ...styles.popper,
    minWidth: '150px',
    /*paddingTop: 10*/
  };

  const onActionButtonClick = action === 'click'
    ? () => setMenuVisible(!menuVisible)
    : undefined;

  const onActionButtonRightClick = action === 'rightClick'
    ? (e:MouseEvent) => {
      setMenuVisible(!menuVisible);
      e.stopPropagation();
      e.preventDefault();
    }
    : undefined;
  /* reformat array by separator. This is done because of avoiding changes in tons of files. */
  const formatterItems = useMemo(() => splitMenuItemsIntoSections(items), [items])

  const ButtonComponentToRender = menuVisible ? HighlightButtonComponent : ButtonComponent;

  return (
    <>
      <ButtonComponentToRender name={name} ref={buttonRef} size={size} onClick={onActionButtonClick} onContextMenu={onActionButtonRightClick}{...(menuVisible ? { useInList } : {})} data-tooltip={useInList ? tooltip : ''}>
        {useInList ? children : tooltip}
        {!hideCaret && !useInList &&
          <>&nbsp;<FontAwesomeIcon icon={menuVisible ? faAngleUp : faAngleDown} /></>
        }
        <span ref={setReferenceElement}></span>
      </ButtonComponentToRender>
      <Portal node={document && document.getElementById('ifms-app-bottom')}>
        {menuVisible &&
          <div
            onMouseMove={(e) => {e.stopPropagation()}}
            ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
            <div ref={menuContainerRef}>
              <Menu
                variant={variant}
                items={formatterItems}
                mode="vertical"
                onMenuItemSelected={onMenuItemSelected}
                subMenuStyle={{
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                }}
                menuStyle={(i: number) => ({
                  maxHeight: height ?? '600px',
                  overflowY: 'hidden',
                  padding: 0,
                  borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
                  paddingTop: 10,
                })}
              />
              <div ref={setArrowElement} style={styles.arrow} />
            </div>
          </div>
        }
      </Portal>
    </>
  );
}

ActionButton.defaultProps = {
  type: 'primary',
  size: 'regular',
  action: 'hover',
  hideCaret: false,
  onItemSelected: () => {},
  variant: 'plain',
};

ActionButton.propTypes = {
  type: PropTypes.oneOf(['primary', 'secondary', 'danger', 'primaryInverse', 'blackInverse','iconButton']),
  size: PropTypes.oneOf(['regular', 'small', 'large','imgStyle','extrasmall']),
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    separator: PropTypes.bool,
  })),
  action: PropTypes.oneOf(['click', 'hover', 'rightClick']).isRequired,
  hideCaret: PropTypes.bool.isRequired,
  onItemSelected: PropTypes.func.isRequired,
  height: PropTypes.string,
  variant: PropTypes.oneOf(['plain', 'highlight']).isRequired,
  tooltip: PropTypes.string,
  useInList: PropTypes.bool,
};

export default React.memo(ActionButton);
